import React, { useEffect, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { useLocation } from 'react-router-dom';
import { RoleProvider, useRoleContext } from '../hooks/useRoleContext';
import Header from './Header/Header';
import Footer from './Footer/Footer';
import Sidebar from './Sidebar/Sidebar';
import SpinnerOverlay from '../components/SpinnerOverlay/SpinnerOverlay';
import './Layout.scss';
import MaintenanceBanner from '../views/Configuration/ServiceOutage/MaintenanceBanner';
import { handleError } from '../utils';
import useAxios from '../hooks/useAxios';
import IdleSessionHandler from '../components/IdleTimeChecker/IdleSessionHandler';

const Layout = ({ children }: any) => {
  const { role, isRoleFetched, isRoleFetching } = useRoleContext();
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [bannerType, setBannerType] = useState(null);
  const [bannerData, setBannerData] = useState(null);
  const toastBottom = useRef(null);
  const { get } = useAxios();
  const location = useLocation();
  const currPathName = location.pathname?.split('/');
  const toggleSideBar = () => {
    setSidebarVisible(false);
  };
  
  useEffect(() => {
    const checkSessionExpired = () => {
      const expired = localStorage.getItem('logout-event');

      if (expired !== null && expired) {
        if (toastBottom.current) {
          toastBottom.current.show({
            severity: 'warn',
            summary: 'Session Expired',
            detail:
              'Your session is no longer active. Please log in again to continue.',
            life: 2000,
          });
        }
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        checkSessionExpired();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  const getServiceOutage = async () => {
    await get('notification-manager/get_service_outage_banner').then((response: any) => {
      const { status } = response;

      if ([200, 201].includes(status)) {
        if (response.data.messages && response.data.messages[0]) {
          setBannerType(response.data.messages[0].banner_type);
          setBannerData(response.data.messages[0]);
        }
      }
    })
      .catch((error: any) => {
        if ([401, 404, 500].includes(error?.request?.status)) {
          handleError(error, toastBottom);
        }
      });
  };

  useEffect(() => {
    if (currPathName?.[1] === 'home') {
      getServiceOutage();
    } else {
      setBannerData(null);
      setBannerType(null);
    }
  }, [currPathName?.[1]]);

  if (isRoleFetched) {
    return (
      <div className="layout-container flex flex-column h-screen">
        <Toast ref={toastBottom} position="bottom-right" />
        {/* Top Header */}
        <Header />

        {/* Below Header: Sidebar + Main Content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar */}
          <Sidebar />

          {/* Main Content Area */}
          <div className="flex flex-column flex-1 overflow-auto mainbodycontent">
            <div className="layout-body">
              {bannerData && <div style={{ paddingTop: '10px' }}><MaintenanceBanner banner={bannerData} /></div>}
              <div className="layout-body-inner">
                {children}
              </div>
            </div>
          </div>
        </div>
      </div>

    );
  } else {
    return (
      <div className="card">
        <SpinnerOverlay />
      </div>
    );
  }
};

// export default Layout;
// Wrap Layout in LayoutProvider
const LayoutWithProvider = ({ children }: any) => (
  <>
    <IdleSessionHandler />
    <RoleProvider>
      <Layout>{children}</Layout>
    </RoleProvider>
  </>
);

export default LayoutWithProvider;
